import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/ProductSearch.css';
import useScrollRestoration from './useScrollRestoration';

const SearchPage = () => {
  useScrollRestoration();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('s');
    const page = parseInt(params.get('page')) || 0;
    const size = parseInt(params.get('size')) || pageSize;
    const sort = params.get('sortBy') || sortBy;
    const order = params.get('sortOrder') || sortOrder;

    setSortBy(sort);
    setSortOrder(order);

    if (searchQuery) {
      setQuery(searchQuery);
      fetchProductsByQuery(searchQuery, page, size, sort, order);
    }
    setSuggestions([]);
  }, [location.search]);

  useEffect(() => {
    if (query.length > 1) {
      fetchSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query]);

  const fetchSuggestions = async (query) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/product/getSuggestions?query=${query}`);
    const data = await response.json();
    setSuggestions(data);
  };

  const handleSearch = () => {
    if (query.length > 1) {
      navigate(
        `/search?s=${query}&page=0&size=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`
      );
      fetchProductsByQuery(query, 0, pageSize, sortBy, sortOrder);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    navigate(`/search?s=${suggestion}&page=0&size=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`);
    fetchProductsByQuery(suggestion, 0, pageSize, sortBy, sortOrder);
  };

  const fetchProductsByQuery = async (query, page, size, sort, order) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/product/searchProducts?query=${query}&page=${page}&size=${size}&sortBy=${sort}&sortOrder=${order}`
    );
    const data = await response.json();

    setProducts(data.content);
    setTotalPages(data.totalPages);
    setTotalElements(data.totalElements);
    setCurrentPage(data.pageable.pageNumber);
    setSuggestions([]);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
    fetchProductsByQuery(query, currentPage, pageSize, e.target.value, sortOrder);
  };

  const handleOrderChange = (e) => {
    setSortOrder(e.target.value);
    fetchProductsByQuery(query, currentPage, pageSize, sortBy, e.target.value);
  };

  const clearFilters = () => {
    setQuery('');
    setSortBy('name');
    setSortOrder('asc');
    navigate(`/search?page=0&size=${pageSize}&sortBy=name&sortOrder=asc`);
    fetchProductsByQuery('', 0, pageSize, 'name', 'asc');
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    navigate(
      `/search?s=${query}&page=${pageNumber}&size=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}`
    );
    fetchProductsByQuery(query, pageNumber, pageSize, sortBy, sortOrder);
  };

  const handleGoToPageChange = (e) => {
    const pageNumber = parseInt(e.target.value) - 1;
    if (pageNumber >= 0 && pageNumber < totalPages) {
      handlePageChange(pageNumber);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const goToProductPage = (productId) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="search-container">
      <div className="search-input">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Search products or categories"
        />
        <button onClick={handleSearch}>Search</button>
        <button onClick={clearFilters} className="clear-filters-btn">
          Clear Filters
        </button>
      </div>

      {query.length > 1 && suggestions.length > 0 && (
        <div className="suggestions-overlay">
          <div className="suggestions">
            {suggestions.map((suggestion, index) => (
              <p key={index} onClick={() => handleSuggestionClick(suggestion)} className="suggestion-item">
                {suggestion}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="sorting-controls">
        <label htmlFor="sortBy">Sort By:</label>
        <select id="sortBy" value={sortBy} onChange={handleSortChange}>
          <option value="name">Name</option>
          <option value="price">Price</option>
          <option value="id">ID</option>
          <option value="discount">Discount</option>
        </select>

        <label htmlFor="sortOrder">Order:</label>
        <select id="sortOrder" value={sortOrder} onChange={handleOrderChange}>
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      {products.length > 0 && (
        <div className="results">
          <div className="product-list">
            {products.map((product) => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => goToProductPage(product.id)}
              >
                <div className="product-image-container">
                  <img
                    src={`/uploads/${product.images[0]?.imageUrl}`}
                    alt={product.name}
                    className="product-image"
                  />
                  {product.discount > 0 && <div className="discount-badge">{product.discount}% OFF</div>}
                </div>
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                {/* <p className="rating-info">
                  <strong>Rating:</strong> {product.averageRating} ⭐ ({product.noOfRating} reviews)
                </p> */}
                <p className="rating-info">
  <strong>{product.averageRating.toFixed(1)}</strong>{' '}
  <span className="stars">
    {Array.from({ length: 5 }, (_, index) =>
      index < Math.round(product.averageRating) ? '★' : '☆'
    ).join('')}
  </span>{' '}
  ({product.noOfRating} ratings)
</p>

                <div className="product-card-footer">
                  {product.discount > 0 ? (
                    <>
                      <span className="original-price">${product.price}</span>
                      <span className="discounted-price">
                        ${(product.price - (product.price * product.discount) / 100).toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="price">${product.price}</span>
                  )}
                  <span className="stock">In stock: {product.stock}</span>
                </div>
                <div className="categories">
                  {product.category.map((cat) => (
                    <span key={cat.id} className="category-tag">
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={currentPage === 0}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
          <span>
            Page {currentPage + 1} of {totalPages}
          </span>
          <input
            type="number"
            value={currentPage + 1}
            onChange={handleGoToPageChange}
            min={1}
            max={totalPages}
            className="go-to-page-input"
          />
          <button
            disabled={currentPage === totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
